import {
  AuthenticationMethod,
  AuthenticationProcessTelecomType,
  CaptchaOptions,
  CardinalApis,
  CardinalBaseSdk,
  CardinalSdk,
  CryptoStrategies,
  DataOwnerWithType,
  KeypairFingerprintV1String,
  KeyPairRecoverer,
  RecoveryDataKey,
  RecoveryDataUseFailureReason,
  RecoveryKeyOptions,
  RecoveryKeySize,
  RecoveryResult,
  BasicSdkOptions,
  BasicToFullSdkOptions,
  HealthcareParty,
  SdkOptions,
  Solution,
  spkiHexKeyToFingerprintV1,
  SpkiHexString,
  StorageFacade,
  User,
  UserGroup,
  XCryptoService,
  XRsaKeypair,
  UserFilters,
} from '@icure/cardinal-sdk'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Unsubscribe } from 'redux'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { revertAll, setSavedCredentials } from '../app'
import { store } from '../store'
import { APPLICATION_ID, ICURE_CLOUD_URL, MSG_GW_URL, PROCESS_ID, SPEC_ID } from '../../constants'
import { startReplications } from '../api/interopApi'

const apiCache: { [key: string]: CardinalSdk } = {}

export class RetinoBridgeCryptoStrategies extends CryptoStrategies {
  private parentId: string | null

  constructor(parentId: string | null = null) {
    super()
    this.parentId = parentId
  }

  generateNewKeyForDataOwner(self: DataOwnerWithType, cryptoPrimitives: XCryptoService): Promise<boolean | XRsaKeypair | 'keyless' | 'parent-delegator'> {
    return Promise.resolve(self.dataOwner.publicKeysForOaepWithSha256.length == 0 && Object.keys(self.dataOwner.aesExchangeKeys).length == 0 && self.dataOwner.publicKey == null)
  }

  async notifyNewKeyCreated(sdk: CardinalApis): Promise<void> {
    const recoveryKey = await sdk.recovery.createRecoveryInfoForAvailableKeyPairs({
      includeParentsKeys: true,
      recoveryKeyOptions: new RecoveryKeyOptions.Generate({ recoveryKeySize: RecoveryKeySize.Bytes32 }),
    })

    const formattedKey = recoveryKey
      .asBase32()
      .match(/.{1,4}/g)
      ?.join('-')

    formattedKey && store.dispatch(setNewlyCreatedRecoveryKey({ recoveryKey: formattedKey }))
  }

  async recoverAndVerifySelfHierarchyKeys(
    keysData: Array<CryptoStrategies.KeyDataRecoveryRequest>,
    cryptoPrimitives: XCryptoService,
    keyPairRecoverer: KeyPairRecoverer,
  ): Promise<{ [dataOwnerId: string]: CryptoStrategies.RecoveredKeyData }> {
    const parentRequests = keysData.filter((kd) => kd.dataOwnerDetails.dataOwner.id === this.parentId)
    const selfRequests = keysData.filter((kd) => kd.dataOwnerDetails.dataOwner.id !== this.parentId)

    const aggregate: { [dataOwnerId: string]: { [pub: SpkiHexString]: XRsaKeypair } } = {}

    // Phase 1: Prompt for parent recovery key if parent keys are missing
    if (parentRequests.some((pr) => pr.unavailableKeys.length > 0)) {
      const parentRequest = parentRequests[0]
      const parentOwner = parentRequest.dataOwnerDetails.dataOwner as HealthcareParty
      const parentName =
        parentOwner.name ?? (parentOwner.lastName ? (parentOwner.firstName ? `${parentOwner.firstName} ${parentOwner.lastName}` : parentOwner.lastName) : parentOwner.id)

      const parentReasons = Array.from({ length: Math.max(1, parentRequest.unavailableKeys.length) }, () => RecoveryDataUseFailureReason.Missing)
      const parentKeys = await this.promptUserForRecoveryKeys(parentReasons, 'parent', parentName)

      if (parentKeys?.length) {
        await this.tryRecoverKeys(parentKeys, keyPairRecoverer, aggregate)
      }
    }

    // Phase 2: Prompt for self recovery key if the user's own keys are unavailable
    const selfStillMissing = selfRequests.some((sr) => {
      const recovered = aggregate[sr.dataOwnerDetails.dataOwner.id]
      return sr.unavailableKeys.some((uk) => !recovered?.[uk.publicKey])
    })

    if (selfStillMissing) {
      const selfRequest = selfRequests[selfRequests.length - 1]
      const selfReasons = Array.from({ length: Math.max(1, selfRequest.unavailableKeys.length) }, () => RecoveryDataUseFailureReason.Missing)
      const selfKeys = await this.promptUserForRecoveryKeys(selfReasons, 'self')

      if (selfKeys?.length) {
        await this.tryRecoverKeys(selfKeys, keyPairRecoverer, aggregate)
      }
    }

    return this.buildRecoveryResult(keysData, aggregate)
  }

  private async tryRecoverKeys(
    recoveryKeys: string[],
    keyPairRecoverer: KeyPairRecoverer,
    aggregate: { [dataOwnerId: string]: { [pub: SpkiHexString]: XRsaKeypair } },
  ): Promise<void> {
    for (const rk of recoveryKeys) {
      let decoded: RecoveryDataKey | undefined
      try {
        decoded = RecoveryDataKey.fromBase32(rk)
      } catch (e) {
        console.warn('Invalid recovery key, skipping:', e)
        continue
      }

      const res = await keyPairRecoverer.recoverWithRecoveryKey(decoded, false)
      if (res instanceof RecoveryResult.Success) {
        const data = res.data
        for (const dataOwnerId of Object.keys(data)) {
          aggregate[dataOwnerId] = aggregate[dataOwnerId] ?? {}
          const perOwner = data[dataOwnerId]
          for (const pub of Object.keys(perOwner)) {
            aggregate[dataOwnerId][pub as SpkiHexString] = perOwner[pub as SpkiHexString]
          }
        }
      }
    }
  }

  private buildRecoveryResult(
    keysData: Array<CryptoStrategies.KeyDataRecoveryRequest>,
    aggregate: { [dataOwnerId: string]: { [pub: SpkiHexString]: XRsaKeypair } },
  ): { [dataOwnerId: string]: CryptoStrategies.RecoveredKeyData } {
    const hasRecovered = Object.keys(aggregate).length > 0 && Object.values(aggregate).some((ownerMap) => Object.keys(ownerMap).length > 0)
    if (!hasRecovered) return {}

    const result: { [dataOwnerId: string]: CryptoStrategies.RecoveredKeyData } = {}
    for (const recoveryRequest of keysData) {
      const dataOwnerId = recoveryRequest.dataOwnerDetails.dataOwner.id
      const perOwnerRecovered = aggregate[dataOwnerId]

      const recoveredForThisOwner: { [fp: KeypairFingerprintV1String]: XRsaKeypair } = {}
      if (perOwnerRecovered) {
        for (const unavailable of recoveryRequest.unavailableKeys) {
          const recoveredKey = perOwnerRecovered[unavailable.publicKey]
          if (recoveredKey) {
            recoveredForThisOwner[spkiHexKeyToFingerprintV1(unavailable.publicKey) as KeypairFingerprintV1String] = recoveredKey
          }
        }
      }

      result[dataOwnerId] = {
        recoveredKeys: recoveredForThisOwner,
        keyAuthenticity: {}, // keep placeholder; fill when authenticity checks are added
      }
    }
    return result
  }

  private async promptUserForRecoveryKeys(reasons: RecoveryDataUseFailureReason[], type: 'parent' | 'self', parentName?: string): Promise<string[] | undefined> {
    const promise = new Promise<string[] | undefined>((resolve) => {
      // eslint-disable-next-line prefer-const
      let unsubscribe: Unsubscribe | undefined
      const handleChange = () => {
        const {
          cardinalApi: { recoveryKeys, recoveryKeyRequest },
        } = store.getState()
        if (!recoveryKeys?.length) {
          // The following happens in case of skip
          if (!recoveryKeyRequest) {
            resolve(undefined)
            unsubscribe?.()
          }
          return
        }
        const normalized = recoveryKeys.map((k) => k.replace(/-/g, '').replace(/0/g, 'O').replace(/1/g, 'I').replace(/8/g, 'B'))
        resolve(normalized)
        unsubscribe?.()
      }
      unsubscribe = store.subscribe(handleChange)
    })

    store.dispatch(askForRecoveryKey({ reasons, type, parentName }))
    return promise
  }
}

class ParentCryptoStrategies extends RetinoBridgeCryptoStrategies {
  async notifyNewKeyCreated(sdk: CardinalApis): Promise<void> {
    const recoveryKey = await sdk.recovery.createRecoveryInfoForAvailableKeyPairs({
      includeParentsKeys: true,
      recoveryKeyOptions: new RecoveryKeyOptions.Generate({ recoveryKeySize: RecoveryKeySize.Bytes32 }),
    })

    const formattedKey = recoveryKey
      .asBase32()
      .match(/.{1,4}/g)
      ?.join('-')

    formattedKey && store.dispatch(setParentRecoveryKey({ recoveryKey: formattedKey }))
  }
}

// Parent SDK is initialized only to generate keys for the parent account itself — no hierarchy needed
function retinobridgeParentSdkOptions(groupSelector: (availableGroups: Array<UserGroup>) => Promise<string>): SdkOptions {
  return {
    useHierarchicalDataOwners: false,
    cryptoStrategies: new ParentCryptoStrategies(),
    groupSelector,
  }
}

export interface CardinalApiState {
  email?: string
  shortToken?: string
  user?: User
  keyPair?: { publicKey: string; privateKey: string }
  authProcess?: CardinalBaseSdk.BaseAuthenticationWithProcessStep
  online: boolean
  invalidEmail: boolean
  invalidToken: boolean
  waitingForToken: boolean
  firstName?: string
  lastName?: string
  dateOfBirth?: number
  mobilePhone?: string
  loginProcessStarted: boolean
  newlyCreatedRecoveryKey?: string
  recoveryKeyRequest?: { reasons: string[]; type: 'parent' | 'self'; parentName?: string }
  recoveryKeys?: string[]
  ehealthPassphrase?: string
  selectedGroupIdToLogin?: string
  availableGroupsToLogin?: UserGroup[]
  environmentSelectionRequested: boolean
  refusedEnvironmentSelection: boolean
  newShortTokenIsRequested?: boolean
  parentKeyInitInProgress?: boolean
  parentCredentialsRequest?: { parentHcpId: string; parentHcpName?: string; parentLogin: string }
  parentAuthKey?: string
  parentRecoveryKey?: string
  parentKeyInitError?: string
  parentHcpName?: string
  dataOwnerId?: string
}

const cardinalApiInitialState: CardinalApiState = {
  email: undefined,
  shortToken: undefined,
  user: undefined,
  keyPair: undefined,
  authProcess: undefined,
  online: false,
  invalidEmail: false,
  invalidToken: false,
  waitingForToken: false,
  firstName: undefined,
  lastName: undefined,
  dateOfBirth: undefined,
  mobilePhone: undefined,
  loginProcessStarted: false,
  newlyCreatedRecoveryKey: undefined,
  recoveryKeyRequest: undefined,
  recoveryKeys: undefined,
  ehealthPassphrase: undefined,
  selectedGroupIdToLogin: undefined,
  availableGroupsToLogin: [],
  environmentSelectionRequested: false,
  refusedEnvironmentSelection: false,
  newShortTokenIsRequested: false,
  parentKeyInitInProgress: undefined,
  parentCredentialsRequest: undefined,
  parentAuthKey: undefined,
  parentRecoveryKey: undefined,
  parentKeyInitError: undefined,
  parentHcpName: undefined,
  dataOwnerId: undefined,
}

function getError(e: Error): FetchBaseQueryError {
  return { status: 'CUSTOM_ERROR', error: e.message, data: e }
}

function retinobridgeBaseSdkOptions(groupSelector: (availableGroups: Array<UserGroup>) => Promise<string>): BasicSdkOptions {
  return { groupSelector }
}

// Child SDK uses hierarchical data owners so it can access data shared through the parent organisation
function retinobridgeToFullSdkOptions(parentId: string | null = null): BasicToFullSdkOptions {
  return {
    useHierarchicalDataOwners: true,
    cryptoStrategies: new RetinoBridgeCryptoStrategies(parentId),
  }
}

function hcpHasPublicKeys(hcp: HealthcareParty): boolean {
  return hcp.publicKeysForOaepWithSha256.length > 0 || Object.keys(hcp.aesExchangeKeys).length > 0 || hcp.publicKey != null
}

export async function guard<T, A, B, C, D, E, F = undefined>(
  guardedInputs: [A?, B?, C?, D?, E?],
  lambda: (constrainedInputs: [A, B, C, D, E]) => Promise<T>,
  fallback: F = undefined as F,
): Promise<{ error: FetchBaseQueryError } | { data: T | F }> {
  if (guardedInputs.some((x) => !x)) {
    return { data: fallback }
  }
  try {
    const res = await lambda(guardedInputs as [A, B, C, D, E])
    const curate = (result: T): T => {
      if (result === null || result === undefined) {
        return null as T
      } else if (Array.isArray(result)) {
        return result.map(curate) as T
      } else {
        return result as T
      }
    }
    return { data: curate(res) }
  } catch (e) {
    console.error(e)
    return { error: getError(e as Error) }
  }
}

export const getApiFromState = async (
  getState: () =>
    | CardinalApiState
    | {
        cardinalApi: CardinalApiState
      }
    | undefined,
): Promise<CardinalSdk | undefined> => {
  const state = getState()
  if (!state) {
    throw new Error('No state found')
  }

  const initialState = 'cardinalApi' in state ? state.cardinalApi : state
  const { user } = initialState

  if (!user) {
    return undefined
  }

  return apiCache[`${user.groupId}/${user.id}`] as CardinalSdk
}

export const cardinalApi = async (getState: () => unknown) => {
  const state = getState() as { cardinalApi: CardinalApiState }
  return await getApiFromState(() => state)
}

const saveLongLivedTokenInLocalStorageIfNeeded = async (api: CardinalSdk, user: User, dispatch: (v: unknown) => void) => {
  const newToken = await api.user.getToken(user.id, 'rememberMe', { tokenValidity: 30 * 24 * 3600 })
  try {
    dispatch(
      setSavedCredentials({
        login: `${user.groupId}/${user.id}`,
        token: newToken,
        tokenTimestamp: +Date.now(),
      }),
    )
  } catch (e) {
    console.log('Could not save long lived token in local storage')
  }
}

export const startAuthentication = createAsyncThunk(
  'cardinalApi/startAuthentication',
  async (
    _payload: {
      captchaToken: Solution
    },
    { getState, dispatch },
  ) => {
    const {
      cardinalApi: { email, firstName, lastName, selectedGroupIdToLogin },
    } = getState() as { cardinalApi: CardinalApiState }
    dispatch(setLoginProcessStarted(true))

    if (!email) {
      throw new Error('The email was not found')
    }

    try {
      const groupSelector = (availableGroups: Array<UserGroup>): Promise<string> =>
        new Promise(async (resolve) => {
          if (selectedGroupIdToLogin) {
            resolve(selectedGroupIdToLogin)
          } else {
            dispatch(setLoginProcessStarted(false))
            dispatch(askForEnvironmentSelection())
            dispatch(setAvailableGroupsToLogin(availableGroups))
          }
        })
      const authenticationStep = await CardinalBaseSdk.initializeWithProcess(
        APPLICATION_ID,
        ICURE_CLOUD_URL,
        MSG_GW_URL,
        SPEC_ID!,
        PROCESS_ID!,
        AuthenticationProcessTelecomType.Email,
        email,
        new CaptchaOptions.Kerberus.Computed({ solution: _payload.captchaToken }),
        { firstName, lastName },
        retinobridgeBaseSdkOptions(groupSelector),
      )

      dispatch(setLoginProcessStarted(false))
      return authenticationStep
    } catch (e) {
      console.error(`Couldn't start authentication: ${e}`)
    } finally {
      dispatch(setLoginProcessStarted(false))
    }
  },
)

async function checkAndHandleParentKeyInit(baseSdk: CardinalBaseSdk, dispatch: (v: unknown) => unknown, getState: () => unknown): Promise<string | null> {
  const user = await baseSdk.user.getCurrentUser()
  const hcpId = user.healthcarePartyId
  if (!hcpId) return null

  const hcp = await baseSdk.healthcareParty.getHealthcareParty(hcpId)
  if (!hcp?.parentId) return null

  const parentHcp = await baseSdk.healthcareParty.getHealthcareParty(hcp.parentId)
  if (!parentHcp || hcpHasPublicKeys(parentHcp)) return hcp.parentId

  await runParentKeyInit(baseSdk, parentHcp, user, dispatch, getState)
  return hcp.parentId
}

export const completeAuthentication = createAsyncThunk('cardinalApi/completeAuthentication', async (_payload, { getState, dispatch }) => {
  const {
    cardinalApi: { authProcess, shortToken },
  } = getState() as { cardinalApi: CardinalApiState }
  dispatch(setLoginProcessStarted(true))

  if (!authProcess) {
    dispatch(setLoginProcessStarted(false))
    throw new Error('No authProcess provided')
  }

  if (!shortToken) {
    dispatch(setLoginProcessStarted(false))
    throw new Error('No token provided')
  }
  try {
    const baseSdk = await authProcess.completeAuthentication(shortToken)

    const parentId = await checkAndHandleParentKeyInit(baseSdk, dispatch, getState)

    const api = await baseSdk.toFullSdk(StorageFacade.usingBrowserLocalStorage(), retinobridgeToFullSdkOptions(parentId))
    const user = await api.user.getCurrentUser()

    const dataOwnerId = parentId ?? user.healthcarePartyId
    if (!dataOwnerId) {
      throw new Error('Could not determine data owner ID: no parent ID and no healthcarePartyId on user')
    }
    dispatch(setDataOwnerId(dataOwnerId))

    apiCache[`${user.groupId}/${user.id}`] = api

    startReplications(api)

    await saveLongLivedTokenInLocalStorageIfNeeded(api, user, dispatch)
    return new User(user)
  } catch (e) {
    console.error(`Couldn't complete authentication: ${e}`)
    throw e
  } finally {
    dispatch(setLoginProcessStarted(false))
  }
})

export const login = createAsyncThunk('cardinalApi/login', async (_, { getState, dispatch }) => {
  const {
    cardinalApi: { email, shortToken, selectedGroupIdToLogin },
  } = getState() as { cardinalApi: CardinalApiState }
  dispatch(setLoginProcessStarted(true))

  if (!email) {
    dispatch(setLoginProcessStarted(false))
    throw new Error('No email provided')
  }

  if (!shortToken) {
    dispatch(setLoginProcessStarted(false))
    throw new Error('No token provided')
  }

  try {
    const groupSelector = (availableGroups: Array<UserGroup>): Promise<string> =>
      new Promise(async (resolve) => {
        if (selectedGroupIdToLogin) {
          resolve(selectedGroupIdToLogin)
        } else {
          dispatch(setLoginProcessStarted(false))
          dispatch(setSavedCredentials(undefined))
          dispatch(askForEnvironmentSelection())
          dispatch(setAvailableGroupsToLogin(availableGroups))
        }
      })

    const baseSdk = await CardinalBaseSdk.initialize(
      APPLICATION_ID,
      ICURE_CLOUD_URL,
      new AuthenticationMethod.UsingCredentials.UsernamePassword(email, shortToken),
      retinobridgeBaseSdkOptions(groupSelector),
    )

    const parentId = await checkAndHandleParentKeyInit(baseSdk, dispatch, getState)

    const api = await baseSdk.toFullSdk(StorageFacade.usingBrowserLocalStorage(), retinobridgeToFullSdkOptions(parentId))
    const user = await api.user.getCurrentUser()

    const dataOwnerId = parentId ?? user.healthcarePartyId
    if (!dataOwnerId) {
      throw new Error('Could not determine data owner ID: no parent ID and no healthcarePartyId on user')
    }
    dispatch(setDataOwnerId(dataOwnerId))

    startReplications(api)

    apiCache[`${user.groupId}/${user.id}`] = api

    dispatch(
      setSavedCredentials({
        login: `${user.groupId}/${user.id}`,
        token: shortToken,
        tokenTimestamp: +Date.now(),
      }),
    )

    return new User(user)
  } catch (e) {
    console.error(`Couldn't login: ${e}`)
    dispatch(revertAll())
    dispatch(resetCredentials())
  } finally {
    dispatch(setLoginProcessStarted(false))
  }
})

const waitForParentAuthKey = (): Promise<string | undefined> => {
  return new Promise<string | undefined>((resolve) => {
    // eslint-disable-next-line prefer-const
    let unsubscribe: Unsubscribe | undefined
    const handleChange = () => {
      const {
        cardinalApi: { parentAuthKey, parentCredentialsRequest, parentKeyInitInProgress },
      } = store.getState()
      // User provided the auth key
      if (parentAuthKey) {
        resolve(parentAuthKey)
        unsubscribe?.()
        return
      }
      // User cancelled (parentCredentialsRequest cleared without providing a key)
      if (!parentCredentialsRequest && !parentKeyInitInProgress) {
        resolve(undefined)
        unsubscribe?.()
      }
    }
    unsubscribe = store.subscribe(handleChange)
  })
}

const waitForParentRecoveryKeyAcknowledged = (): Promise<void> => {
  return new Promise<void>((resolve) => {
    // eslint-disable-next-line prefer-const
    let unsubscribe: Unsubscribe | undefined
    const handleChange = () => {
      const {
        cardinalApi: { parentRecoveryKey },
      } = store.getState()
      if (!parentRecoveryKey) {
        resolve()
        unsubscribe?.()
      }
    }
    unsubscribe = store.subscribe(handleChange)
  })
}

async function runParentKeyInit(baseSdk: CardinalBaseSdk, parentHcp: HealthcareParty, childUser: User, dispatch: (v: unknown) => unknown, getState: () => unknown): Promise<void> {
  // Find the parent User by healthcarePartyId
  const parentUserIds = await baseSdk.user.matchUsersBy(UserFilters.byHealthcarePartyId(parentHcp.id))
  const parentUserId = parentUserIds[0]
  if (!parentUserId) {
    dispatch(setParentKeyInitError({ error: 'auth.parentKeyInit.errors.parentUserNotFound' }))
    throw new Error('Parent user not found')
  }
  const parentLogin = `${childUser.groupId}/${parentUserId}`

  const parentHcpName: string = parentHcp.lastName
    ? parentHcp.firstName
      ? `${parentHcp.firstName} ${parentHcp.lastName}`
      : parentHcp.lastName
    : parentHcp.name
      ? parentHcp.name
      : parentHcp.id

  // Show parent auth modal and wait for auth key, with retry on failure
  // eslint-disable-next-line no-constant-condition
  while (true) {
    dispatch(askForParentCredentials({ parentHcpId: parentHcp.id, parentHcpName, parentLogin }))
    const parentAuthKey = await waitForParentAuthKey()
    if (!parentAuthKey) {
      // User cancelled
      dispatch(resetParentKeyInitState())
      dispatch(revertAll())
      dispatch(resetCredentials())
      throw new Error('Parent key initialization cancelled')
    }

    // Initialize full CardinalSdk as parent to generate keys
    const {
      cardinalApi: { selectedGroupIdToLogin },
    } = getState() as { cardinalApi: CardinalApiState }

    const groupSelector = (availableGroups: Array<UserGroup>): Promise<string> =>
      new Promise(async (resolve) => {
        if (selectedGroupIdToLogin) {
          resolve(selectedGroupIdToLogin)
        } else if (availableGroups.length === 1 && availableGroups[0].groupId) {
          resolve(availableGroups[0].groupId)
        } else {
          dispatch(askForEnvironmentSelection())
          dispatch(setAvailableGroupsToLogin(availableGroups))
        }
      })

    try {
      await CardinalSdk.initialize(
        APPLICATION_ID,
        ICURE_CLOUD_URL,
        new AuthenticationMethod.UsingCredentials.UsernameLongToken(parentLogin, parentAuthKey),
        StorageFacade.usingBrowserLocalStorage(),
        retinobridgeParentSdkOptions(groupSelector),
      )
      break
    } catch (e) {
      dispatch(setParentKeyInitError({ error: 'auth.parentKeyInit.errors.authFailed' }))
      continue
    }
  }

  // Wait for user to acknowledge parent recovery key
  await waitForParentRecoveryKeyAcknowledged()

  dispatch(resetParentKeyInitState())
}

export const logout = createAsyncThunk('cardinalApi/logout', async (_payload, { dispatch }) => {
  dispatch(revertAll())
  dispatch(resetCredentials())
})

export const cardinalApiRtk = createSlice({
  name: 'cardinalApi',
  initialState: cardinalApiInitialState,
  reducers: {
    setNewlyCreatedRecoveryKey: (
      state,
      {
        payload: { recoveryKey },
      }: PayloadAction<{
        recoveryKey: string | undefined
      }>,
    ) => {
      state.newlyCreatedRecoveryKey = recoveryKey
    },
    askForRecoveryKey: (state, { payload: { reasons, type, parentName } }: PayloadAction<{ reasons: string[]; type: 'parent' | 'self'; parentName?: string }>) => {
      state.recoveryKeyRequest = { reasons, type, parentName }
      state.recoveryKeys = undefined
    },
    provideRecoveryKey: (state, { payload: { recoveryKeys } }: PayloadAction<{ recoveryKeys: string[] }>) => {
      state.recoveryKeyRequest = undefined
      state.recoveryKeys = recoveryKeys
    },
    markRecoveryKeyAsLost: (state) => {
      state.recoveryKeyRequest = undefined
      state.recoveryKeys = []
    },
    setToken: (state, { payload: { token } }: PayloadAction<{ token: string }>) => {
      state.shortToken = token
      state.invalidToken = false
      state.newShortTokenIsRequested = undefined
    },
    setEmail: (state, { payload: { email } }: PayloadAction<{ email: string }>) => {
      state.email = email
      state.invalidEmail = false
    },
    setUser: (state, { payload: { user } }: PayloadAction<{ user: User }>) => {
      state.user = user
      state.online = !!user
    },
    resetCredentials: (state) => {
      state.online = false
      state.user = undefined
      state.selectedGroupIdToLogin = undefined
      state.newShortTokenIsRequested = undefined
      state.availableGroupsToLogin = []
      state.dataOwnerId = undefined
      state.parentKeyInitInProgress = undefined
      state.parentCredentialsRequest = undefined
      state.parentAuthKey = undefined
      state.parentRecoveryKey = undefined
      state.parentKeyInitError = undefined
      state.parentHcpName = undefined
    },
    setDataOwnerId: (state, { payload }: PayloadAction<string>) => {
      state.dataOwnerId = payload
    },
    setLoginProcessStarted(state, { payload: status }: PayloadAction<boolean>) {
      state.loginProcessStarted = status
    },
    setWaitingForToken(state, { payload: status }: PayloadAction<boolean>) {
      state.waitingForToken = status
    },
    setEhealthPassphrase: (state, { payload: { ehealthPassphrase } }: PayloadAction<{ ehealthPassphrase: string }>) => {
      state.ehealthPassphrase = ehealthPassphrase
    },
    provideSelectedEnvironment: (state, { payload }: PayloadAction<string>) => {
      state.selectedGroupIdToLogin = payload
      state.environmentSelectionRequested = false
    },
    refuseEnvironmentSelection: (state) => {
      state.refusedEnvironmentSelection = true
      state.selectedGroupIdToLogin = undefined
      state.environmentSelectionRequested = false
      state.waitingForToken = false
      state.newShortTokenIsRequested = false
      setSavedCredentials(undefined)
    },
    askForEnvironmentSelection: (state) => {
      state.environmentSelectionRequested = true
      state.waitingForToken = false
      state.refusedEnvironmentSelection = false
    },
    setAvailableGroupsToLogin(state, { payload: groups }: PayloadAction<UserGroup[]>) {
      state.availableGroupsToLogin = groups
    },
    askNewShortToken: (state) => {
      state.shortToken = undefined
      state.invalidToken = false
      state.newShortTokenIsRequested = true
    },
    markNewShortTokenRequestAsDone: (state) => {
      state.newShortTokenIsRequested = false
    },
    askForParentCredentials: (state, { payload }: PayloadAction<{ parentHcpId: string; parentHcpName?: string; parentLogin: string }>) => {
      state.parentCredentialsRequest = payload
      state.parentHcpName = payload.parentHcpName
      state.parentKeyInitInProgress = true
      state.parentKeyInitError = undefined
      state.parentAuthKey = undefined
    },
    provideParentAuthKey: (state, { payload: { authKey } }: PayloadAction<{ authKey: string }>) => {
      state.parentAuthKey = authKey
      state.parentCredentialsRequest = undefined
    },
    cancelParentKeyInit: (state) => {
      state.parentKeyInitInProgress = undefined
      state.parentCredentialsRequest = undefined
      state.parentAuthKey = undefined
      state.parentRecoveryKey = undefined
      state.parentKeyInitError = undefined
    },
    setParentRecoveryKey: (state, { payload: { recoveryKey } }: PayloadAction<{ recoveryKey: string }>) => {
      state.parentRecoveryKey = recoveryKey
    },
    clearParentRecoveryKey: (state) => {
      state.parentRecoveryKey = undefined
    },
    setParentKeyInitError: (state, { payload: { error } }: PayloadAction<{ error: string }>) => {
      state.parentKeyInitError = error
    },
    resetParentKeyInitState: (state) => {
      state.parentKeyInitInProgress = undefined
      state.parentCredentialsRequest = undefined
      state.parentAuthKey = undefined
      state.parentRecoveryKey = undefined
      state.parentKeyInitError = undefined
      state.parentHcpName = undefined
    },
  },
  extraReducers: (builder) => {
    builder.addCase(startAuthentication.fulfilled, (state, { payload: authProcess }) => {
      state.authProcess = authProcess
      state.waitingForToken = true
      state.newShortTokenIsRequested = false
    })
    builder.addCase(startAuthentication.rejected, (state, {}) => {
      state.invalidEmail = true
      state.newShortTokenIsRequested = false
    })
    builder.addCase(completeAuthentication.fulfilled, (state, { payload: user }) => {
      state.user = user as User
      state.online = !!user
      state.waitingForToken = false
      state.newShortTokenIsRequested = false
    })
    builder.addCase(completeAuthentication.rejected, (state, {}) => {
      state.invalidToken = true
      state.newShortTokenIsRequested = false
    })
    builder.addCase(login.fulfilled, (state, { payload: user }) => {
      state.user = user as User
      state.online = !!user
      state.newShortTokenIsRequested = false
    })
    builder.addCase(login.rejected, (state, {}) => {
      state.invalidToken = true
      state.online = false
      state.newShortTokenIsRequested = false
    })
  },
})

export const {
  setNewlyCreatedRecoveryKey,
  askForRecoveryKey,
  provideRecoveryKey,
  markRecoveryKeyAsLost,
  setToken,
  setEmail,
  resetCredentials,
  setDataOwnerId,
  setLoginProcessStarted,
  setWaitingForToken,
  setEhealthPassphrase,
  provideSelectedEnvironment,
  refuseEnvironmentSelection,
  askForEnvironmentSelection,
  setAvailableGroupsToLogin,
  askNewShortToken,
  markNewShortTokenRequestAsDone,
  askForParentCredentials,
  provideParentAuthKey,
  cancelParentKeyInit,
  setParentRecoveryKey,
  clearParentRecoveryKey,
  setParentKeyInitError,
  resetParentKeyInitState,
} = cardinalApiRtk.actions
